import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { iconFor, formatRel } from '../components/BellIcon'
import { useEntityList, useEntitySave, useEntityDelete, now } from '../api/hooks/useEntity'
import { ENTITY } from '../types/entities'
import type { Notification } from '../types/entities'

/** Полный список уведомлений с фильтром «непрочитанные/все» и mark-as-read. */
export function NotificationsPage() {
  const { t } = useTranslation()
  const list = useEntityList<Notification>(ENTITY.notification)
  const save = useEntitySave<Notification>(ENTITY.notification)
  const del = useEntityDelete<Notification>(ENTITY.notification)
  const navigate = useNavigate()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const items = useMemo(() => {
    const all = (list.data ?? [])
      .filter((n) => !n.deletedAt)
      .sort((a, b) => b.createdAt - a.createdAt)
    return filter === 'unread' ? all.filter((n) => !n.readAt) : all
  }, [list.data, filter])

  const unreadCount = (list.data ?? []).filter((n) => !n.deletedAt && !n.readAt).length

  const markAllRead = () => {
    const unread = (list.data ?? []).filter((n) => !n.deletedAt && !n.readAt)
    if (unread.length === 0) return
    const updates = unread.map((n) => ({ ...n, readAt: now(), updatedAt: now() }))
    save.mutate(updates as Notification[])
  }

  const handleClick = (n: Notification) => {
    if (!n.readAt) save.mutate({ ...n, readAt: now(), updatedAt: now() })
    if (n.link) navigate(n.link)
  }

  return (
    <AppShell
      title={t('notificationsPage.title')}
      subtitle={unreadCount > 0 ? t('notificationsPage.unreadSubtitle', { count: unreadCount }) : t('notificationsPage.allRead')}
      actions={
        <button
          onClick={markAllRead}
          disabled={unreadCount === 0}
          className="btn-outline text-sm"
        >
          ✓ {t('notificationsPage.markAllRead')}
        </button>
      }
    >
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
            filter === 'all' ? 'bg-primary-soft text-primary font-medium' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('notificationsPage.filterAll', { count: (list.data ?? []).filter((n) => !n.deletedAt).length })}
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
            filter === 'unread' ? 'bg-primary-soft text-primary font-medium' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('notificationsPage.filterUnread', { count: unreadCount })}
        </button>
      </div>

      {list.isLoading ? (
        <div className="text-center py-10 text-gray-500">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
          <div className="text-5xl mb-3">📭</div>
          <h3 className="font-semibold text-gray-900 mb-2">
            {filter === 'unread' ? t('notificationsPage.emptyUnread') : t('notificationsPage.emptyAll')}
          </h3>
          <p className="text-sm text-gray-600 mb-4 px-4">
            {t('notificationsPage.emptyHint')}
          </p>
          <Link to="/app" className="btn-outline text-sm inline-block">
            ← {t('notificationsPage.toHome')}
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          {items.map((n) => (
            <div
              key={n.id}
              className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${
                !n.readAt ? 'bg-primary-soft/20' : ''
              }`}
            >
              <span className="text-xl flex-shrink-0 mt-0.5">{iconFor(n.type)}</span>
              <button
                onClick={() => handleClick(n)}
                className="flex-1 text-left min-w-0"
              >
                <div className={`text-sm leading-snug ${!n.readAt ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                  {n.title}
                </div>
                <div className="text-sm text-gray-600 mt-0.5">
                  {n.body}
                </div>
                <div className="text-xs text-gray-400 mt-1.5">
                  {formatRel(n.createdAt, t)}
                  {n.link && <span className="text-primary ml-2">→ {t('notificationsPage.goTo')}</span>}
                </div>
              </button>
              {!n.readAt && (
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
              )}
              <button
                onClick={() => del.mutate(n)}
                className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                title={t('common.delete')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  )
}
